import { useParams } from "react-router-dom";
import React, { useEffect, useState, useRef, useCallback } from "react";
import "./ProjectList.scss";
import Navbar from "../../components/Navbar/MainNavbar/Navbar";
import { allProjectsData } from "../../constants/data";
import Footer from "../../components/Footer/Footer";

import { referenceProjects } from "../../constants/projectImages";
import IntroVideo from "../../components/IntroVideo/IntroVideo";
import useTranslate from "../../hooks/useTranslate";

/* ───── image map ───── */
/* ───── spec label translation keys ───── */
const SPEC_LABEL_MAP = {
  PROJECT: "specs_PROJECT",
  LOCATION: "specs_LOCATION",
  DATE: "specs_DATE",
  AREA: "specs_AREA",
};

const ProjectList = () => {
  const { category, projectName: pathName } = useParams();
  const { translate, language } = useTranslate();
  const isRTL = language === "fa";

  /* ── project data ── */
  const [imgSrc, setImgSrc] = useState(null);
  useEffect(() => {
    const found = allProjectsData?.find((p) => p.projectName === pathName && p.link === `/${category}`);
    if (found) setImgSrc(found);
  }, [pathName, category]);

  /* ── images ── */
  const arrayData = referenceProjects.find(p => p.projectName === pathName && p.category === category)?.files || [];
  const images = arrayData.map((item) => item.src || item);

  /* ══════════════════════════════════════════════════════════
     THUMBNAIL STRIP: measure container to decide how many fit
  ══════════════════════════════════════════════════════════ */
  const thumbStripRef = useRef(null);
  const [thumbLimit, setThumbLimit] = useState(images.length);

  useEffect(() => {
    const calcLimit = () => {
      if (!thumbStripRef.current) return;
      const containerWidth = thumbStripRef.current.offsetWidth;

      let thumbSize = 92;
      const gap = 4;
      const screenWidth = window.innerWidth;

      if (screenWidth <= 480) {
        thumbSize = 64;
      } else if (screenWidth <= 768) {
        thumbSize = 72;
      }

      const maxFit = Math.floor((containerWidth + gap) / (thumbSize + gap));

      if (images.length <= maxFit) {
        setThumbLimit(images.length);
      } else {
        setThumbLimit(Math.max(maxFit - 1, 1));
      }
    };

    calcLimit();
    window.addEventListener("resize", calcLimit);
    return () => window.removeEventListener("resize", calcLimit);
  }, [images.length]);

  const visibleThumbs = images.slice(0, thumbLimit);
  const remainingCount = images.length - thumbLimit;
  const hasMore = remainingCount > 0;

  /* ════════════════════════════════════════════
     HERO SLIDER
  ════════════════════════════════════════════ */
  const [heroIdx, setHeroIdx] = useState(0);
  const heroTimer = useRef(null);

  const startHeroTimer = useCallback(() => {
    clearInterval(heroTimer.current);
    heroTimer.current = setInterval(() => {
      setHeroIdx((prev) => (prev + 1) % images.length);
    }, 2000);
  }, [images.length]);

  useEffect(() => {
    if (images.length > 1) startHeroTimer();
    return () => clearInterval(heroTimer.current);
  }, [images.length, startHeroTimer]);

  useEffect(() => { setHeroIdx(0); setModalIdx(0); setModalOpen(false); }, [pathName, category]);

  const goHero = (idx) => {
    setHeroIdx(idx);
    startHeroTimer();
  };

  /* ════════════════════════════════════════════
     MODAL / LIGHTBOX
  ════════════════════════════════════════════ */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIdx, setModalIdx] = useState(0);
  const openModal = (startIdx = 0) => {
    setModalIdx(startIdx);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    if (!modalOpen) return;
    const handleKey = (e) => {
      if (e.key === "ArrowRight") {
        setModalIdx((prev) => (prev + 1) % images.length);
      } else if (e.key === "ArrowLeft") {
        setModalIdx((prev) => (prev - 1 + images.length) % images.length);
      } else if (e.key === "Escape") {
        closeModal();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [modalOpen, images.length]);

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [modalOpen]);

  /* ── scroll to top ── */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathName, category]);

  return (
    <>
      <Navbar />
      <div
        className={`projectList ${isRTL ? "rtl-mode" : "ltr-mode"}`}
        id="projectsList"
      >
        {/* ═══════════ HERO SLIDER ═══════════ */}
        {images.length > 0 ? (
          <div className="hero-slider">
            <div className="hero-slider__track">
              {images.map((src, idx) => (
                <div
                  key={idx}
                  className={`hero-slider__slide ${
                    idx === heroIdx ? "active" : ""
                  }`}
                >
                  <img src={src} alt={`${imgSrc?.titleFa || imgSrc?.title || pathName} - ${idx + 1}`} loading={idx === 0 ? "eager" : "lazy"} />
                </div>
              ))}
            </div>

            {images.length > 1 && (
              <div className="hero-slider__dots">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    className={`hero-slider__dot ${
                      idx === heroIdx ? "active" : ""
                    }`}
                    onClick={() => goHero(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}

            <div className="hero-slider__counter">
              {heroIdx + 1} / {images.length}
            </div>
          </div>
        ) : imgSrc?.videoSrc ? (
          <div className="top">
            <IntroVideo videoSrc={imgSrc?.videoSrc} />
          </div>
        ) : (
          <div className="top">
            <img src={imgSrc?.src} className="topImage" alt="" />
          </div>
        )}

        {/* ═══════════ THUMBNAIL STRIP ═══════════ */}
        {images.length > 0 && (
          <div className="thumb-strip" ref={thumbStripRef}>
            <div className="thumb-strip__row">
              {visibleThumbs.map((src, idx) => (
                <div
                  key={idx}
                  className="thumb-strip__item"
                  onClick={() => openModal(idx)}
                >
                  <img src={src} alt={`${imgSrc?.titleFa || imgSrc?.title || pathName} - ${idx + 1}`} />
                </div>
              ))}

              {hasMore && (
                <div
                  className="thumb-strip__item thumb-strip__item--more"
                  onClick={() => openModal(thumbLimit)}
                >
                  <img src={images[thumbLimit]} alt="more" />
                  <div className="thumb-strip__more-overlay">
                    <span>
                      +{remainingCount}{" "}
                      {isRTL
                        ? "عکس"
                        : remainingCount === 1
                        ? "photo"
                        : "photos"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ PROJECT SPECS (شناسنامه پروژه) ═══════════ */}
        {imgSrc?.specs && imgSrc.specs.length > 0 && (
          <div className="project-specs">
            <div className="project-specs__grid">
              {imgSrc.specs.map((spec, idx) => {
                const trimmedKey = spec.key.trim();
                const valTransKey = `spec_val_${pathName}_${trimmedKey}`;

                return (
                  <div key={idx} className="project-specs__item">
                    <span className="project-specs__label">
                      {translate(SPEC_LABEL_MAP[trimmedKey]) || trimmedKey}
                    </span>
                    <span className="project-specs__value">
                      {translate(valTransKey) || spec.value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══════════ PROJECT DETAILS ═══════════ */}
        <div className="projectDetails">
          <h1>{isRTL && imgSrc?.titleFa ? imgSrc.titleFa : translate(imgSrc?.title)}</h1>
          {imgSrc?.desc && <p className="project-desc">{translate(imgSrc.desc)}</p>}
        </div>

        {imgSrc?.secondVideo ? (
          <div className="projectDetails">
            <IntroVideo videoSrc={imgSrc?.secondVideo} />
          </div>
        ) : null}
      </div>

      {/* ═══════════ MODAL / LIGHTBOX ═══════════ */}
      {modalOpen && (
        <div className="lightbox-modal" onClick={closeModal}>
          <div
            className="lightbox-modal__content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="lightbox-modal__close"
              onClick={closeModal}
              aria-label="Close lightbox"
            >
              ✕
            </button>

            <div className="lightbox-modal__image-wrapper">
              <img
                src={images[modalIdx]}
                alt={`${imgSrc?.titleFa || imgSrc?.title || pathName} - ${modalIdx + 1}`}
                className="lightbox-modal__image"
              />
            </div>

            <div className="lightbox-modal__counter">
              {modalIdx + 1} / {images.length}
            </div>

            <div className="lightbox-modal__thumbstrip">
              {images.map((src, idx) => (
                <div
                  key={idx}
                  className={`lightbox-modal__thumbstrip-item ${
                    idx === modalIdx ? "active" : ""
                  }`}
                  onClick={() => setModalIdx(idx)}
                >
                  <img src={src} alt={`${imgSrc?.titleFa || imgSrc?.title || pathName} - ${idx + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ProjectList;

